import LocalizationService from './LocalizationService';

/**
 * Uygulama için içerik yükleme işlemlerini yöneten servis sınıfı
 * Application katmanında yer alır
 */
export class ContentService {
  private static instance: ContentService;
  private contentCache: Map<string, { content: string, timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 dakika cache süresi

  private constructor() {
    // Singleton yapı
  }

  /**
   * Singleton instance'ı getir
   */
  public static getInstance(): ContentService {
    if (!ContentService.instance) {
      ContentService.instance = new ContentService();
    }
    return ContentService.instance;
  }

  /**
   * Cache'i temizle (dil değişikliğinde kullanılabilir)
   */
  public clearCache(): void {
    this.contentCache.clear();
    console.log('[ContentService] İçerik cache temizlendi');
  }

  /**
   * Belirli bir dil için HTML dosyasını yükler
   * @param fileName - Dosya adı (uzantısız)
   * @param language - Yüklenecek dil (varsayılan olarak mevcut dil)
   * @returns Promise<string> - HTML içeriği
   */
  public async loadHtmlContent(
    fileName: string,
    language: string = LocalizationService.getCurrentLanguage()
  ): Promise<string> {
    try {
      const cacheKey = `${fileName}_${language}`;
      
      // Cache'den kontrol et
      const cachedContent = this.contentCache.get(cacheKey);
      if (cachedContent && (Date.now() - cachedContent.timestamp) < this.CACHE_TTL) {
        console.log(`[ContentService] Cache'den HTML içeriği alınıyor: ${cacheKey}`);
        return cachedContent.content;
      }
      
      // İlk olarak belirtilen dilde dosyayı yüklemeyi dene
      const publicUrl = process.env.PUBLIC_URL || '';
      const url = `${publicUrl}/infrastructure/i18n/${language}/${fileName}.html`;
      console.log(`[ContentService] PUBLIC_URL: ${publicUrl}`);
      console.log(`[ContentService] HTML dosyası yükleniyor: ${url}`);
      
      // Fetch isteğine timeout ekle
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 saniye timeout
      
      try {
        const response = await fetch(url, { 
          signal: controller.signal,
          headers: { 'Cache-Control': 'no-cache' } // Browser cache kullanmayı önle
        });
        clearTimeout(timeoutId);
        
        console.log(`[ContentService] Fetch yanıtı: status=${response.status}, ok=${response.ok}, headers=`, Object.fromEntries(response.headers.entries()));
        
        // Eğer dosya bulunamazsa, İngilizce versiyonuna geri dön
        if (!response.ok && language !== 'en') {
          console.warn(`[ContentService] ${fileName}.html dosyası ${language} dilinde bulunamadı, İngilizce versiyona geçiliyor.`);
          return this.loadHtmlContent(fileName, 'en');
        }
        
        if (!response.ok) {
          console.error(`[ContentService] HTML dosyası yüklenemedi: ${fileName}.html (${response.status} ${response.statusText})`);
          return this.generateErrorContent('İçerik Bulunamadı', 'İstenen içerik şu anda görüntülenemiyor. Lütfen daha sonra tekrar deneyin.');
        }
        
        const content = await response.text();
        console.log(`[ContentService] Fetch sonucunda alınan içerik uzunluğu: ${content.length}`);
        console.log(`[ContentService] İçerik ilk 100 karakter: "${content.substring(0, 100)}..."`);
        
        if (!content || content.trim() === '') {
          console.warn(`[ContentService] ${fileName}.html dosyası boş içerik döndürdü`);
          return this.generateErrorContent('İçerik Bulunamadı', 'İstenen içerik şu anda görüntülenemiyor. Lütfen daha sonra tekrar deneyin.');
        }
        
        // İçeriği stil sınıfları ile geliştir
        const enhancedContent = this.enhanceHtmlContent(content);
        
        // Cache'e ekle
        this.contentCache.set(cacheKey, {
          content: enhancedContent, 
          timestamp: Date.now()
        });
        
        console.log(`[ContentService] ${fileName}.html yüklendi, içerik uzunluğu: ${content.length}`);
        return enhancedContent;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        console.error(`[ContentService] HTML içeriği yüklenirken fetch hatası:`, fetchError);
        throw fetchError;
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error(`[ContentService] HTML içeriği yükleme zaman aşımına uğradı`);
        return this.generateErrorContent('Zaman Aşımı', 'İçerik yüklenirken zaman aşımı oluştu. Lütfen daha sonra tekrar deneyin.');
      }
      
      console.error(`[ContentService] HTML içeriği yüklenirken hata oluştu:`, error);
      return this.generateErrorContent('Yükleme Hatası', 'İçerik yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    }
  }

  /**
   * HTML içeriğine stil sınıfları ekler
   * @param html - HTML içeriği
   * @returns string - Geliştirilmiş HTML içeriği
   */
  private enhanceHtmlContent(html: string): string {
    // Başlıkları ve paragrafları geliştir
    let enhancedHtml = html
      .replace(/<h2>/g, '<h2 class="terms-section-title">')
      .replace(/<h3>/g, '<h3 class="terms-subsection-title">')
      .replace(/<p>/g, '<p class="terms-paragraph">')
      .replace(/<ul>/g, '<ul class="terms-list">')
      .replace(/<ol>/g, '<ol class="terms-list">')
      .replace(/<li>/g, '<li class="terms-list-item">');
      
    // Önemli notlara özel stiller ekle
    enhancedHtml = enhancedHtml.replace(
      /<p>(\s*)(Not:|ÖNEMLİ:|DİKKAT:|NOT:)(.+?)<\/p>/g, 
      '<div class="terms-note">$2$3</div>'
    );
    
    // Önemli uyarılar için stiller ekle
    enhancedHtml = enhancedHtml.replace(
      /<p>(\s*)(Uyarı:|UYARI:|Dikkat!|DİKKAT!)(.+?)<\/p>/g, 
      '<div class="terms-important">$2$3</div>'
    );
    
    return enhancedHtml;
  }

  /**
   * Hata durumlarında gösterilecek HTML içeriği oluşturur
   * @param title - Hata başlığı
   * @param message - Hata mesajı
   * @returns string - HTML içeriği
   */
  private generateErrorContent(title: string, message: string): string {
    return `
      <div class="terms-html-content">
        <h1>${title}</h1>
        <p class="terms-paragraph">${message}</p>
      </div>
    `;
  }
}

// Singleton instance export et
export default ContentService.getInstance(); 